import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Skeleton } from "@/components/ui/skeleton";
import { Scissors, Clock, MapPin, Phone, ArrowRight, ArrowLeft, Check, Loader2, Star, Shield, Bell, Users } from "lucide-react";
import { format, isBefore, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const STEPS = ["Service", "Date & heure", "Vos infos", "Confirmation"];

export default function BookingPage() {
  const { slug } = useParams();
  const [shopData, setShopData] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmedAppt, setConfirmedAppt] = useState(null);

  useEffect(() => {
    API.get(`/book/${slug}`)
      .then(r => { setShopData(r.data.shop); setServices(r.data.services); setLoading(false); })
      .catch(() => { setError("Salon non trouv\u00e9"); setLoading(false); });
  }, [slug]);

  useEffect(() => {
    if (selectedService && selectedDate) {
      setSlotsLoading(true);
      setSelectedTime(null);
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      API.get(`/book/${slug}/slots?date=${dateStr}&service_id=${selectedService.id}`)
        .then(r => { setAvailableSlots(r.data.slots || []); setSlotsLoading(false); })
        .catch(() => { setAvailableSlots([]); setSlotsLoading(false); });
    }
  }, [selectedService, selectedDate, slug]);

  const handleBook = async () => {
    if (!clientName.trim() || !clientPhone.trim()) { toast.error("Remplissez tous les champs"); return; }
    setSubmitting(true);
    try {
      const res = await API.post(`/book/${slug}`, {
        service_id: selectedService.id, date: format(selectedDate, "yyyy-MM-dd"),
        start_time: selectedTime, client_name: clientName, client_phone: clientPhone,
      });
      setConfirmedAppt(res.data);
      setConfirmed(true);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la r\u00e9servation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-white" data-testid="booking-loading">
      <div className="bg-[#09090B] px-6 py-8"><Skeleton className="h-8 w-48 bg-white/10" /><Skeleton className="h-4 w-32 bg-white/10 mt-2" /></div>
      <div className="max-w-2xl mx-auto p-6 space-y-4">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-white"><div className="text-center"><Scissors size={40} className="text-muted-foreground/30 mx-auto mb-4" /><p className="text-lg font-semibold">{error}</p></div></div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="booking-page">
      {/* Shop Header */}
      <div className="bg-[#09090B] text-white">
        <div className="max-w-2xl mx-auto px-6 py-8">
          <div className="flex items-center gap-2 mb-4">
            <Scissors size={18} className="text-[#2563EB]" />
            <span className="font-heading font-bold text-xs text-white/40">CutIt</span>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{shopData?.name}</h1>
          <div className="flex items-center gap-1 mt-2">
            {[...Array(5)].map((_, i) => <Star key={i} size={13} className="text-yellow-400 fill-yellow-400" />)}
            <span className="text-xs text-white/50 ml-1.5">4.8 (120 avis)</span>
          </div>
          {shopData?.address && <p className="text-white/50 text-sm mt-3 flex items-center gap-1.5"><MapPin size={13} /> {shopData.address}</p>}
          {shopData?.phone && <p className="text-white/50 text-sm mt-1 flex items-center gap-1.5"><Phone size={13} /> {shopData.phone}</p>}
        </div>
      </div>

      {/* Trust Badges */}
      <div className="max-w-2xl mx-auto px-6 py-3 flex items-center gap-4 text-[11px] text-muted-foreground overflow-x-auto border-b border-border">
        <span className="flex items-center gap-1 whitespace-nowrap"><Shield size={12} className="text-green-500" /> R&eacute;servation s&eacute;curis&eacute;e</span>
        <span className="flex items-center gap-1 whitespace-nowrap"><Users size={12} className="text-blue-500" /> +120 clients satisfaits</span>
        <span className="flex items-center gap-1 whitespace-nowrap"><Bell size={12} className="text-orange-500" /> Rappel automatique</span>
      </div>

      {/* Progress Steps */}
      <div className="max-w-2xl mx-auto px-6 py-4 border-b border-border">
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${i < step ? "bg-green-500 text-white" : i === step ? "bg-[#2563EB] text-white scale-110" : "bg-muted text-muted-foreground"}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={`text-[11px] hidden sm:block ${i <= step ? "text-foreground font-medium" : "text-muted-foreground"}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`w-6 sm:w-10 h-px ${i < step ? "bg-green-500" : "bg-border"}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Step 0: Select Service */}
        {step === 0 && (
          <div className="step-active">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-1">Choisissez un service</h2>
            <p className="text-sm text-muted-foreground mb-6">S&eacute;lectionnez la prestation souhait&eacute;e.</p>
            <div className="space-y-3">
              {services.map((s) => (
                <button key={s.id} onClick={() => { setSelectedService(s); setStep(1); }}
                  className="w-full p-5 rounded-xl border text-left transition-all hover:border-blue-300 hover:shadow-soft group border-border bg-white"
                  data-testid={`booking-service-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold group-hover:text-[#2563EB] transition-colors">{s.name}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Clock size={11} /> {s.duration} min</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-mono font-bold text-lg">{s.price}&euro;</p>
                      <ArrowRight size={16} className="text-muted-foreground group-hover:text-[#2563EB] transition-colors" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Date & Time */}
        {step === 1 && (
          <div className="step-active">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-[#2563EB]/10 rounded-lg px-3 py-1.5">
                <p className="text-xs font-semibold text-[#2563EB]">{selectedService?.name} &middot; {selectedService?.duration} min &middot; {selectedService?.price}&euro;</p>
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-1">Choisissez une date</h2>
            <p className="text-sm text-muted-foreground mb-6">S&eacute;lectionnez un jour et un cr&eacute;neau disponible.</p>
            <div className="grid md:grid-cols-2 gap-6">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => { setSelectedDate(d); setSelectedTime(null); }}
                disabled={(d) => isBefore(d, startOfDay(new Date()))} locale={fr}
                className="rounded-xl border border-border p-3 bg-white" data-testid="booking-calendar" />
              <div>
                {selectedDate ? (
                  <>
                    <p className="text-sm font-semibold mb-3 capitalize">{format(selectedDate, "EEEE d MMMM", { locale: fr })}</p>
                    {slotsLoading ? (
                      <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-11 w-full" />)}</div>
                    ) : availableSlots.length === 0 ? (
                      <div className="bg-muted/50 rounded-xl p-6 text-center"><p className="text-sm text-muted-foreground">Aucun cr&eacute;neau disponible ce jour.</p></div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.map((slot) => (
                          <button key={slot} onClick={() => setSelectedTime(slot)}
                            className={`time-slot font-mono text-sm py-3 rounded-lg border text-center font-medium transition-all ${selectedTime === slot ? "selected bg-[#2563EB] text-white border-[#2563EB] scale-[1.02]" : "border-border hover:border-blue-300 bg-white"}`}
                            data-testid={`slot-${slot}`}>{slot}</button>
                        ))}
                      </div>
                    )}
                  </>
                ) : <p className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-6 text-center">S&eacute;lectionnez une date pour voir les cr&eacute;neaux.</p>}
              </div>
            </div>
            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(0)} className="gap-2" data-testid="booking-back-1"><ArrowLeft size={16} /> Retour</Button>
              <Button onClick={() => setStep(2)} disabled={!selectedTime} className="rounded-full px-8 bg-[#2563EB] hover:bg-blue-700 gap-2" data-testid="booking-next-1">Continuer <ArrowRight size={16} /></Button>
            </div>
          </div>
        )}

        {/* Step 2: Client Info */}
        {step === 2 && (
          <div className="step-active">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-1">Vos informations</h2>
            <p className="text-sm text-muted-foreground mb-6">Pour confirmer votre rendez-vous.</p>
            <div className="space-y-5 max-w-md">
              <div>
                <label className="text-sm font-medium mb-2 block">Votre nom *</label>
                <Input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Jean Dupont" className="h-12" data-testid="booking-client-name" />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Votre t&eacute;l&eacute;phone *</label>
                <Input value={clientPhone} onChange={e => setClientPhone(e.target.value)} placeholder="06 12 34 56 78" className="h-12" data-testid="booking-client-phone" />
              </div>
            </div>
            <div className="mt-8 bg-[#F8F8F8] rounded-2xl p-6 max-w-md border border-border">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-4">R&eacute;capitulatif</p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span className="font-medium">{selectedService?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-medium capitalize">{selectedDate && format(selectedDate, "EEE d MMM", { locale: fr })}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Heure</span><span className="font-mono font-bold">{selectedTime}</span></div>
                <div className="flex justify-between border-t border-border pt-3"><span className="text-muted-foreground">Prix</span><span className="font-mono font-bold text-xl">{selectedService?.price}&euro;</span></div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-8">
              <Button variant="ghost" onClick={() => setStep(1)} className="gap-2" data-testid="booking-back-2"><ArrowLeft size={16} /> Retour</Button>
              <Button onClick={handleBook} disabled={submitting} className="rounded-full px-8 bg-[#2563EB] hover:bg-blue-700 gap-2" data-testid="booking-confirm-btn">
                {submitting ? <><Loader2 size={16} className="animate-spin" /> R&eacute;servation...</> : <>Confirmer <Check size={16} /></>}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && confirmed && (
          <div className="text-center py-8 step-active">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6 animate-scale-in">
              <Check size={36} className="text-green-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">R&eacute;servation confirm&eacute;e !</h2>
            <p className="text-muted-foreground text-sm max-w-md mx-auto mb-3">Votre rendez-vous est confirm&eacute;.</p>
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 text-xs font-medium px-4 py-2 rounded-full mb-8 border border-green-200">
              <Bell size={14} /> Un SMS de confirmation a &eacute;t&eacute; envoy&eacute; &middot; Rappel automatique avant le rdv
            </div>
            <div className="bg-[#F8F8F8] rounded-2xl p-6 max-w-sm mx-auto text-left border border-border">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Salon</span><span className="font-medium">{shopData?.name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Service</span><span className="font-medium">{confirmedAppt?.service_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-medium">{confirmedAppt?.date}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Heure</span><span className="font-mono font-bold">{confirmedAppt?.start_time} - {confirmedAppt?.end_time}</span></div>
                <div className="flex justify-between border-t border-border pt-3"><span className="text-muted-foreground">Prix</span><span className="font-mono font-bold text-lg">{confirmedAppt?.price}&euro;</span></div>
              </div>
            </div>
            <Button onClick={() => { setStep(0); setConfirmed(false); setSelectedService(null); setSelectedDate(null); setSelectedTime(null); setClientName(""); setClientPhone(""); }}
              variant="outline" className="rounded-full mt-8 gap-2" data-testid="booking-new-btn">Faire une autre r&eacute;servation</Button>
          </div>
        )}
      </div>

      {/* Mobile sticky CTA info */}
      {step === 0 && <div className="fixed bottom-0 inset-x-0 p-3 bg-white/80 backdrop-blur-sm border-t border-border lg:hidden text-center"><p className="text-xs text-muted-foreground">S&eacute;lectionnez un service pour r&eacute;server</p></div>}
    </div>
  );
}
